// src/pages/legal/TermsOfService.js
import React from 'react'
import { Box, Container, Typography, Card, CardContent, Divider, Stack } from '@mui/material'
import { useLanguage } from '../../contexts/LanguageContext'

export default function TermsOfService() {
    const effectiveDate = 'January 5, 2026' // change when needed
    const supportEmail = 'support@mojopayr.com' // change when needed

    const { t, lang } = useLanguage()
    const isFr = lang === 'fr'
    const tt = (key, enFallback, frFallback) => t(key, null, isFr ? frFallback : enFallback)

    const sectionTitleSx = {
        fontWeight: 900,
        color: 'rgba(255,255,255,0.92)',
        mt: 2.4,
        mb: 0.8,
        letterSpacing: 0.2,
    }

    const bodySx = {
        color: 'rgba(255,255,255,0.78)',
        lineHeight: 1.9,
        fontSize: { xs: '0.98rem', md: '1.02rem' },
        wordBreak: 'break-word',
        overflowWrap: 'anywhere',
    }

    const liSx = { mb: 0.8 }

    return (
        <Box sx={{ py: { xs: 5, md: 7 } }}>
            <Container maxWidth="md">
                <Typography variant="h4" sx={{ fontWeight: 950, color: 'white', mb: 1 }}>
                    {tt('legal.terms.title', 'Terms of Service', "Conditions d’utilisation")}
                </Typography>

                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    sx={{ mb: 3, alignItems: { sm: 'center' } }}
                >
                    <Typography sx={{ color: 'rgba(255,255,255,0.72)' }}>
                        {tt(
                            'legal.terms.subtitle',
                            'The rules for using MojoPayr services.',
                            "Les règles d’utilisation des services MojoPayr."
                        )}
                    </Typography>

                    <Typography sx={{ color: 'rgba(255,255,255,0.55)' }}>
                        {tt('legal.terms.effective', 'Effective Date:', 'Date d’effet :')} {effectiveDate}
                    </Typography>
                </Stack>

                <Card
                    sx={{
                        borderRadius: 6,
                        background: 'rgba(255,255,255,0.06)',
                        border: '1px solid rgba(255,255,255,0.10)',
                        backdropFilter: 'blur(10px)',
                    }}
                >
                    <CardContent sx={{ p: { xs: 2.4, md: 3.2 } }}>
                        <Typography sx={bodySx}>
                            {tt(
                                'legal.terms.intro',
                                'These Terms of Service govern your use of MojoPayr’s website, mobile applications, and services (the “Service”). By using the Service, you agree to these Terms.',
                                "Ces conditions régissent votre utilisation du site, des applications mobiles et des services de MojoPayr (le « Service »). En utilisant le Service, vous acceptez ces conditions."
                            )}
                        </Typography>

                        <Divider sx={{ my: 2.2, borderColor: 'rgba(255,255,255,0.10)' }} />

                        <Typography sx={sectionTitleSx}>
                            {tt('legal.terms.s1', '1. Eligibility', '1. Éligibilité')}
                        </Typography>
                        <Typography sx={bodySx}>
                            {tt(
                                'legal.terms.s1.p1',
                                'You must be at least 18 years old and able to enter a legally binding agreement to use the Service. You agree that you will use the Service only where it is permitted by law.',
                                "Vous devez avoir au moins 18 ans et être en mesure de conclure un accord juridiquement contraignant pour utiliser le Service. Vous acceptez d’utiliser le Service uniquement là où la loi l’autorise."
                            )}
                        </Typography>

                        <Typography sx={sectionTitleSx}>
                            {tt('legal.terms.s2', '2. Your Account', '2. Votre compte')}
                        </Typography>
                        <Typography sx={bodySx}>
                            {tt(
                                'legal.terms.s2.p1',
                                'You are responsible for maintaining the security of your account and keeping your login details confidential. You agree to provide accurate information and keep it up to date. We may require identity verification to use certain features.',
                                "Vous êtes responsable de la sécurité de votre compte et de la confidentialité de vos identifiants. Vous acceptez de fournir des informations exactes et de les maintenir à jour. Une vérification d’identité peut être requise pour certaines fonctionnalités."
                            )}
                        </Typography>

                        <Box component="ul" sx={{ mt: 1.2, mb: 0, pl: 2.2, ...bodySx }}>
                            <li style={liSx}>{tt('legal.terms.s2.li1', 'Do not share your password or PIN', 'Ne partagez pas votre mot de passe ou code PIN')}</li>
                            <li style={liSx}>{tt('legal.terms.s2.li2', 'Notify us if you suspect unauthorized access', 'Prévenez-nous si vous suspectez un accès non autorisé')}</li>
                            <li style={liSx}>{tt('legal.terms.s2.li3', 'You are responsible for activity under your account', 'Vous êtes responsable des activités effectuées via votre compte')}</li>
                        </Box>

                        <Typography sx={sectionTitleSx}>
                            {tt('legal.terms.s3', '3. Using the Service', '3. Utilisation du Service')}
                        </Typography>
                        <Typography sx={bodySx}>
                            {tt(
                                'legal.terms.s3.p1',
                                'The Service allows you to send, receive, and manage money and related financial features based on availability in your region. We may change, suspend, or discontinue features at any time.',
                                "Le Service vous permet d’envoyer, de recevoir et de gérer de l’argent, ainsi que des fonctionnalités financières associées, selon la disponibilité dans votre région. Nous pouvons modifier, suspendre ou arrêter des fonctionnalités à tout moment."
                            )}
                        </Typography>

                        <Typography sx={sectionTitleSx}>
                            {tt('legal.terms.s4', '4. Prohibited Activities', '4. Activités interdites')}
                        </Typography>
                        <Typography sx={bodySx}>
                            {tt(
                                'legal.terms.s4.p1',
                                'You agree not to use the Service for illegal, harmful, or abusive activity. This includes:',
                                "Vous acceptez de ne pas utiliser le Service pour des activités illégales, nuisibles ou abusives. Cela inclut :"
                            )}
                        </Typography>

                        <Box component="ul" sx={{ mt: 1.2, mb: 0, pl: 2.2, ...bodySx }}>
                            <li style={liSx}>{tt('legal.terms.s4.li1', 'Fraud, scams, or deceptive activity', 'Fraude, escroqueries ou activités trompeuses')}</li>
                            <li style={liSx}>{tt('legal.terms.s4.li2', 'Money laundering or terrorist financing', 'Blanchiment d’argent ou financement du terrorisme')}</li>
                            <li style={liSx}>{tt('legal.terms.s4.li3', 'Using stolen payment methods or unauthorized accounts', 'Utilisation de moyens de paiement volés ou de comptes non autorisés')}</li>
                            <li style={liSx}>{tt('legal.terms.s4.li4', 'Attempting to bypass security or access controls', 'Tentative de contournement de la sécurité ou des contrôles d’accès')}</li>
                            <li style={liSx}>{tt('legal.terms.s4.li5', 'Violating any law, regulation, or third-party rights', 'Violation de toute loi, réglementation ou droits de tiers')}</li>
                        </Box>

                        <Typography sx={sectionTitleSx}>
                            {tt('legal.terms.s5', '5. Fees, Limits, and Availability', '5. Frais, limites et disponibilité')}
                        </Typography>
                        <Typography sx={bodySx}>
                            {tt(
                                'legal.terms.s5.p1',
                                'Fees, limits, and processing times may apply depending on your location, payment method, and transaction type. These may change over time. If a fee applies, we will display it before you complete the transaction when possible.',
                                "Des frais, des limites et des délais de traitement peuvent s’appliquer selon votre localisation, le moyen de paiement et le type de transaction. Ces éléments peuvent évoluer. Si des frais s’appliquent, nous les afficherons avant la confirmation de la transaction lorsque possible."
                            )}
                        </Typography>

                        <Typography sx={sectionTitleSx}>
                            {tt('legal.terms.s6', '6. Transactions and Errors', '6. Transactions et erreurs')}
                        </Typography>
                        <Typography sx={bodySx}>
                            {tt(
                                'legal.terms.s6.p1',
                                'You are responsible for confirming transaction details before sending money. Some transfers may not be reversible. If you believe a transaction is unauthorized or incorrect, contact us as soon as possible so we can review it.',
                                "Vous êtes responsable de vérifier les détails avant d’envoyer de l’argent. Certains transferts peuvent être irréversibles. Si vous pensez qu’une transaction est non autorisée ou incorrecte, contactez-nous dès que possible pour que nous puissions l’examiner."
                            )}
                        </Typography>

                        <Typography sx={sectionTitleSx}>
                            {tt('legal.terms.s7', '7. Suspension and Termination', '7. Suspension et résiliation')}
                        </Typography>
                        <Typography sx={bodySx}>
                            {tt(
                                'legal.terms.s7.p1',
                                'We may suspend or terminate your account if we believe:',
                                "Nous pouvons suspendre ou résilier votre compte si nous estimons que :"
                            )}
                        </Typography>

                        <Box component="ul" sx={{ mt: 1.2, mb: 0, pl: 2.2, ...bodySx }}>
                            <li style={liSx}>{tt('legal.terms.s7.li1', 'Your account is being used in a risky or unauthorized way', 'Votre compte est utilisé de manière risquée ou non autorisée')}</li>
                            <li style={liSx}>{tt('legal.terms.s7.li2', 'You provided false or misleading information', 'Vous avez fourni des informations fausses ou trompeuses')}</li>
                            <li style={liSx}>{tt('legal.terms.s7.li3', 'We are required to do so by law or regulation', 'La loi ou la réglementation nous y oblige')}</li>
                            <li style={liSx}>{tt('legal.terms.s7.li4', 'You violated these Terms', 'Vous avez violé ces conditions')}</li>
                        </Box>

                        <Typography sx={bodySx} style={{ marginTop: 10 }}>
                            {tt(
                                'legal.terms.s7.p2',
                                'You may stop using the Service at any time. Some information may still be retained as required by law.',
                                "Vous pouvez arrêter d’utiliser le Service à tout moment. Certaines informations peuvent être conservées conformément à la loi."
                            )}
                        </Typography>

                        <Typography sx={sectionTitleSx}>
                            {tt('legal.terms.s8', '8. Privacy', '8. Confidentialité')}
                        </Typography>
                        <Typography sx={bodySx}>
                            {tt(
                                'legal.terms.s8.p1',
                                'Your use of the Service is also governed by our Privacy Policy. Please review it to understand how we handle your information.',
                                "Votre utilisation du Service est aussi régie par notre Politique de confidentialité. Veuillez la consulter pour comprendre comment nous traitons vos informations."
                            )}
                        </Typography>

                        <Typography sx={sectionTitleSx}>
                            {tt('legal.terms.s9', '9. Disclaimers', '9. Exclusions de responsabilité')}
                        </Typography>
                        <Typography sx={bodySx}>
                            {tt(
                                'legal.terms.s9.p1',
                                'The Service is provided on an “as is” and “as available” basis. We do not guarantee that the Service will be uninterrupted, timely, or error-free.',
                                "Le Service est fourni « en l’état » et « selon disponibilité ». Nous ne garantissons pas un service continu, ponctuel ou sans erreur."
                            )}
                        </Typography>

                        <Typography sx={sectionTitleSx}>
                            {tt('legal.terms.s10', '10. Limitation of Liability', '10. Limitation de responsabilité')}
                        </Typography>
                        <Typography sx={bodySx}>
                            {tt(
                                'legal.terms.s10.p1',
                                'To the maximum extent permitted by law, MojoPayr will not be liable for indirect, incidental, special, consequential, or punitive damages, or any loss of profits, revenue, or data, arising out of or related to your use of the Service.',
                                "Dans la mesure maximale permise par la loi, MojoPayr ne peut être tenu responsable des dommages indirects, accessoires, spéciaux, consécutifs ou punitifs, ni de toute perte de profits, revenus ou données, résultant de votre utilisation du Service."
                            )}
                        </Typography>

                        <Typography sx={sectionTitleSx}>
                            {tt('legal.terms.s11', '11. Changes to These Terms', '11. Modifications des conditions')}
                        </Typography>
                        <Typography sx={bodySx}>
                            {tt(
                                'legal.terms.s11.p1',
                                'We may update these Terms from time to time. We will post updates on this page and update the effective date. Your continued use of the Service means you accept the updated Terms.',
                                "Nous pouvons mettre à jour ces conditions. Nous publierons les mises à jour sur cette page et mettrons à jour la date d’effet. En continuant à utiliser le Service, vous acceptez les conditions mises à jour."
                            )}
                        </Typography>

                        <Typography sx={sectionTitleSx}>
                            {tt('legal.terms.s12', '12. Contact Us', '12. Contact')}
                        </Typography>
                        <Typography sx={bodySx}>
                            {tt(
                                'legal.terms.s12.p1',
                                'If you have questions about these Terms, contact us at:',
                                "Si vous avez des questions sur ces conditions, contactez-nous à :"
                            )}
                        </Typography>

                        <Typography sx={{ ...bodySx, mt: 1 }}>
                            {tt('legal.terms.emailLabel', 'Email:', 'E-mail :')}{' '}
                            <Box
                                component="a"
                                href={`mailto:${supportEmail}`}
                                sx={{
                                    color: 'rgba(255,255,255,0.92)',
                                    textDecoration: 'underline',
                                    textUnderlineOffset: '3px',
                                }}
                            >
                                {supportEmail}
                            </Box>
                        </Typography>

                        <Divider sx={{ my: 2.2, borderColor: 'rgba(255,255,255,0.10)' }} />

                        <Typography sx={{ color: 'rgba(255,255,255,0.55)', lineHeight: 1.75, fontSize: '0.92rem' }}>
                            {tt(
                                'legal.terms.footer',
                                'These Terms are general and may be updated to match the laws and requirements of the regions where the Service operates.',
                                "Ces conditions sont générales et peuvent être mises à jour pour respecter les lois et exigences des régions où le Service opère."
                            )}
                        </Typography>
                    </CardContent>
                </Card>
            </Container>
        </Box>
    )
}

